export const accountingOverviewEn = {
  "accounting.overview.nav": "Overview",
  "accounting.overview.eyebrow": "Your accounting workspace",
  "accounting.overview.title": "Overview",
  "accounting.overview.description": "Bring source documents, report drafts and the next accounting actions together.",
  "accounting.overview.scopedDescription": "Documents and reporting for {{company}} · {{period}}.",
  "accounting.overview.manageScope": "Manage company & periods",
  "accounting.overview.selectScope": "Choose the accounting period",
  "accounting.overview.selectScopeDescription": "Select a company, project and accounting period in the top bar. Period accounting uses that period; live outstanding invoices include all periods. Create a project or period if needed.",
  "accounting.overview.periodSummary": "Accounting period summary",
  "accounting.overview.documents": "Documents",
  "accounting.overview.documentsHint": "Files assigned to this period",
  "accounting.overview.extracted": "Fields extracted",
  "accounting.overview.extractedHint": "Files with saved source fields",
  "accounting.overview.reportFiles": "Saved report files",
  "accounting.overview.reportFilesHint": "Includes separate output formats",
  "accounting.overview.approved": "Approved",
  "accounting.overview.approvedHint": "Report files marked approved",
  "accounting.overview.attentionHint": "Issues recorded in the selected period's files and reports.",
  "accounting.overview.noFlags": "No recorded issues in these results",
  "accounting.overview.noFlagsHint": "This does not establish that all documents are present or that the period is reconciled. Unvalidated drafts still need review.",
  "accounting.overview.extractionFailed": "Document extraction failed. Inspect the source and retry processing.",
  "accounting.overview.missingOutputs": "A requested output format is missing. Inspect the report's delivery details.",
  "accounting.overview.checksFailed": "Report validation failed. Inspect the recorded findings.",
  "accounting.overview.reportedExceptions": "The report contains unresolved findings. Inspect its supporting evidence.",
  "accounting.overview.moreAttention": "Open Documents and Reports to inspect the remaining recorded issues.",
  "accounting.overview.inspect": "Inspect",
  "accounting.overview.reportSeparation": "Saving a file, validating its content and approving it are separate steps.",
  "accounting.overview.noReports": "No saved reports for this period",
  "accounting.overview.noReportsHint": "Prepare a report from the period's documents. Its saved outputs will appear here.",
  "accounting.overview.report": "Report",
  "accounting.overview.delivery": "Delivery",
  "accounting.overview.validation": "Validation",
  "accounting.overview.review": "Review",
  "accounting.overview.partial": "Missing outputs",
  "accounting.overview.saved": "File saved",
  "accounting.overview.passed": "Passed",
  "accounting.overview.warnings": "Warnings",
  "accounting.overview.failed": "Failed",
  "accounting.overview.notValidated": "Not fully validated",
  "accounting.overview.draft": "Draft",
  "accounting.overview.readyForReview": "Ready for review",
  "accounting.overview.blocked": "Blocked",
  "accounting.overview.sourceHint": "Uploading a document and extracting its fields are separate steps.",
  "accounting.overview.manageDocuments": "Open documents",
  "accounting.overview.noDocuments": "No documents are assigned to this period yet. Open Documents to add source files.",
  "accounting.overview.fieldsAvailable": "Source fields available",
  "accounting.overview.pendingExtraction": "Not extracted yet",
  "accounting.overview.loadError": "The period overview could not be loaded",
  "accounting.overview.retryDescription": "Counts are unavailable. Try again; this view also retries automatically.",
  "accounting.overview.agent": "Accounting Agent",
  "accounting.overview.prepareTitle": "Prepare a source-linked draft.",
  "accounting.overview.agentDescription": "Use the assigned accounting agent and the existing reporting pipeline for this company and period.",
  "accounting.overview.agentSources": "Inspect authorized documents and reusable extracted facts.",
  "accounting.overview.agentCode": "Write and run report-specific code to calculate the results.",
  "accounting.overview.agentSave": "Save the draft, source references and available checks.",
  "accounting.overview.prepare": "Prepare trial balance",
  "accounting.overview.draftBoundary": "Opens a scoped report chat. A generated draft is not financial approval or a completed period close.",
  "accounting.overview.readOnly": "Report preparation requires project editing access and an open period.",
  "accounting.overview.otherReports": "Choose another report",
} as const

export const accountingOverviewEt: Record<keyof typeof accountingOverviewEn, string> = {
  "accounting.overview.nav": "Ülevaade",
  "accounting.overview.eyebrow": "Sinu raamatupidamise tööruum",
  "accounting.overview.title": "Ülevaade",
  "accounting.overview.description": "Algdokumendid, aruannete mustandid ja järgmised tegevused ühes kohas.",
  "accounting.overview.scopedDescription": "Ettevõtte {{company}} dokumendid ja aruanded · {{period}}.",
  "accounting.overview.manageScope": "Halda ettevõtet ja perioode",
  "accounting.overview.selectScope": "Vali arvestusperiood",
  "accounting.overview.selectScopeDescription": "Vali ülaribalt ettevõte, projekt ja arvestusperiood. Perioodi raamatupidamine kasutab valitud perioodi; praegused tasumata arved hõlmavad kõiki perioode. Vajaduse korral loo projekt või periood.",
  "accounting.overview.periodSummary": "Arvestusperioodi kokkuvõte",
  "accounting.overview.documents": "Dokumendid",
  "accounting.overview.documentsHint": "Selle perioodiga seotud failid",
  "accounting.overview.extracted": "Väljad eraldatud",
  "accounting.overview.extractedHint": "Salvestatud lähteandmetega failid",
  "accounting.overview.reportFiles": "Salvestatud aruandefailid",
  "accounting.overview.reportFilesHint": "Eri vormingud loetakse eraldi",
  "accounting.overview.approved": "Kinnitatud",
  "accounting.overview.approvedHint": "Kinnitatuks märgitud aruandefailid",
  "accounting.overview.attentionHint": "Valitud perioodi failides ja aruannetes registreeritud probleemid.",
  "accounting.overview.noFlags": "Nendes tulemustes pole probleeme registreeritud",
  "accounting.overview.noFlagsHint": "See ei kinnita, et kõik dokumendid on olemas või perioodi andmed on kooskõlastatud. Valideerimata mustandid vajavad endiselt ülevaatamist.",
  "accounting.overview.extractionFailed": "Dokumendi andmete eraldamine ebaõnnestus. Kontrolli allikat ja proovi uuesti.",
  "accounting.overview.missingOutputs": "Soovitud väljundvorming puudub. Kontrolli aruande väljundite üksikasju.",
  "accounting.overview.checksFailed": "Aruande valideerimine ebaõnnestus. Vaata registreeritud tähelepanekuid.",
  "accounting.overview.reportedExceptions": "Aruandes on lahendamata tähelepanekuid. Kontrolli alusandmeid.",
  "accounting.overview.moreAttention": "Ülejäänud probleemide vaatamiseks ava dokumendid ja aruanded.",
  "accounting.overview.inspect": "Vaata",
  "accounting.overview.reportSeparation": "Faili salvestamine, sisu valideerimine ja kinnitamine on eraldi toimingud.",
  "accounting.overview.noReports": "Selle perioodi aruandeid pole veel salvestatud",
  "accounting.overview.noReportsHint": "Koosta perioodi dokumentide põhjal aruanne. Salvestatud väljundid kuvatakse siin.",
  "accounting.overview.report": "Aruanne",
  "accounting.overview.delivery": "Väljund",
  "accounting.overview.validation": "Valideerimine",
  "accounting.overview.review": "Ülevaatamine",
  "accounting.overview.partial": "Väljundid puuduvad",
  "accounting.overview.saved": "Fail salvestatud",
  "accounting.overview.passed": "Läbitud",
  "accounting.overview.warnings": "Hoiatused",
  "accounting.overview.failed": "Ebaõnnestus",
  "accounting.overview.notValidated": "Pole täielikult valideeritud",
  "accounting.overview.draft": "Mustand",
  "accounting.overview.readyForReview": "Ülevaatamiseks valmis",
  "accounting.overview.blocked": "Blokeeritud",
  "accounting.overview.sourceHint": "Dokumendi üleslaadimine ja väljade eraldamine on eraldi toimingud.",
  "accounting.overview.manageDocuments": "Ava dokumendid",
  "accounting.overview.noDocuments": "Selle perioodiga pole veel dokumente seotud. Failide lisamiseks ava dokumendid.",
  "accounting.overview.fieldsAvailable": "Lähteandmed on saadaval",
  "accounting.overview.pendingExtraction": "Andmed pole veel eraldatud",
  "accounting.overview.loadError": "Perioodi ülevaadet ei saanud laadida",
  "accounting.overview.retryDescription": "Arvud pole saadaval. Proovi uuesti; vaade proovib ka automaatselt uuesti.",
  "accounting.overview.agent": "Raamatupidamisagent",
  "accounting.overview.prepareTitle": "Koosta allikatega seotud mustand.",
  "accounting.overview.agentDescription": "Kasuta selle ettevõtte ja perioodi jaoks määratud raamatupidamisagenti ning olemasolevat aruandlusprotsessi.",
  "accounting.overview.agentSources": "Kontrolli lubatud dokumente ja varem eraldatud andmeid.",
  "accounting.overview.agentCode": "Kirjuta ja käivita tulemuste arvutamiseks aruandepõhine kood.",
  "accounting.overview.agentSave": "Salvesta mustand, allikaviited ja olemasolevad kontrollitulemused.",
  "accounting.overview.prepare": "Koosta käibeandmik",
  "accounting.overview.draftBoundary": "Avab valitud andmetega aruandevestluse. Loodud mustand ei tähenda finantsandmete kinnitamist ega perioodi sulgemist.",
  "accounting.overview.readOnly": "Aruande koostamiseks on vaja projekti muutmisõigust ja avatud perioodi.",
  "accounting.overview.otherReports": "Vali teine aruanne",
}

export const accountingOverviewRu: Record<keyof typeof accountingOverviewEn, string> = {
  "accounting.overview.nav": "Обзор",
  "accounting.overview.eyebrow": "Рабочая область бухгалтера",
  "accounting.overview.title": "Обзор",
  "accounting.overview.description": "Первичные документы, черновики отчётов и следующие действия в одном месте.",
  "accounting.overview.scopedDescription": "Документы и отчётность компании {{company}} · {{period}}.",
  "accounting.overview.manageScope": "Компания и периоды",
  "accounting.overview.selectScope": "Выберите учётный период",
  "accounting.overview.selectScopeDescription": "Выберите компанию, проект и учётный период в верхней панели. Учёт за период использует выбранный период; текущая задолженность по счетам включает все периоды. При необходимости создайте проект или период.",
  "accounting.overview.periodSummary": "Сводка за учётный период",
  "accounting.overview.documents": "Документы",
  "accounting.overview.documentsHint": "Файлы, относящиеся к периоду",
  "accounting.overview.extracted": "Поля извлечены",
  "accounting.overview.extractedHint": "Файлы с сохранёнными исходными полями",
  "accounting.overview.reportFiles": "Сохранённые файлы отчётов",
  "accounting.overview.reportFilesHint": "Разные форматы учитываются отдельно",
  "accounting.overview.approved": "Согласовано",
  "accounting.overview.approvedHint": "Файлы отчётов со статусом согласования",
  "accounting.overview.attentionHint": "Проблемы, зарегистрированные в файлах и отчётах выбранного периода.",
  "accounting.overview.noFlags": "В этих результатах проблемы не зарегистрированы",
  "accounting.overview.noFlagsHint": "Это не подтверждает наличие всех документов или завершение сверки за период. Непроверенные черновики по-прежнему требуют проверки.",
  "accounting.overview.extractionFailed": "Не удалось извлечь данные документа. Проверьте источник и повторите обработку.",
  "accounting.overview.missingOutputs": "Отсутствует запрошенный формат. Проверьте сведения о результатах отчёта.",
  "accounting.overview.checksFailed": "Отчёт не прошёл проверку. Изучите зарегистрированные замечания.",
  "accounting.overview.reportedExceptions": "В отчёте есть неустранённые замечания. Проверьте подтверждающие данные.",
  "accounting.overview.moreAttention": "Откройте документы и отчёты, чтобы изучить остальные замечания.",
  "accounting.overview.inspect": "Проверить",
  "accounting.overview.reportSeparation": "Сохранение файла, проверка его содержимого и согласование — отдельные действия.",
  "accounting.overview.noReports": "За этот период ещё нет сохранённых отчётов",
  "accounting.overview.noReportsHint": "Подготовьте отчёт по документам периода. Сохранённые результаты появятся здесь.",
  "accounting.overview.report": "Отчёт",
  "accounting.overview.delivery": "Результат",
  "accounting.overview.validation": "Проверка",
  "accounting.overview.review": "Согласование",
  "accounting.overview.partial": "Не все результаты готовы",
  "accounting.overview.saved": "Файл сохранён",
  "accounting.overview.passed": "Пройдена",
  "accounting.overview.warnings": "Предупреждения",
  "accounting.overview.failed": "Ошибка",
  "accounting.overview.notValidated": "Проверка не завершена",
  "accounting.overview.draft": "Черновик",
  "accounting.overview.readyForReview": "Готов к проверке",
  "accounting.overview.blocked": "Заблокирован",
  "accounting.overview.sourceHint": "Загрузка документа и извлечение его полей — отдельные действия.",
  "accounting.overview.manageDocuments": "Открыть документы",
  "accounting.overview.noDocuments": "К этому периоду ещё не привязаны документы. Откройте документы, чтобы добавить исходные файлы.",
  "accounting.overview.fieldsAvailable": "Исходные поля доступны",
  "accounting.overview.pendingExtraction": "Данные ещё не извлечены",
  "accounting.overview.loadError": "Не удалось загрузить обзор периода",
  "accounting.overview.retryDescription": "Количество записей недоступно. Повторите попытку; обзор также обновится автоматически.",
  "accounting.overview.agent": "Бухгалтерский агент",
  "accounting.overview.prepareTitle": "Подготовьте черновик со ссылками на источники.",
  "accounting.overview.agentDescription": "Используйте назначенного бухгалтерского агента и существующий процесс отчётности для этой компании и периода.",
  "accounting.overview.agentSources": "Изучить доступные документы и ранее извлечённые данные.",
  "accounting.overview.agentCode": "Написать и выполнить код для расчёта показателей отчёта.",
  "accounting.overview.agentSave": "Сохранить черновик, ссылки на источники и доступные результаты проверок.",
  "accounting.overview.prepare": "Подготовить оборотно-сальдовую ведомость",
  "accounting.overview.draftBoundary": "Открывает чат отчёта в выбранной области. Создание черновика не означает согласование финансовых данных или закрытие периода.",
  "accounting.overview.readOnly": "Для подготовки отчёта нужны права на редактирование проекта и открытый период.",
  "accounting.overview.otherReports": "Выбрать другой отчёт",
}
